import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas, isRutaRestricted } from '../lib/rutas';
import { Calendar, TrendingUp, FileText, BarChart2, Clock, User, Filter, X, UserPlus, RefreshCw } from 'lucide-react';

interface ContractItem {
  id: string;
  client_name: string;
  cpf: string;
  loan_amount: number;
  total_amount: number;
  created_at: string;
  seller_email: string;
  is_renewal: boolean;
}

interface DailySale {
  date: string;
  ruta: number;
  totalContracts: number;
  totalAmount: number;
  contracts: ContractItem[];
}

export default function DailySales() {
  const { user } = useAuth();
  const [sales, setSales] = useState<DailySale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [selectedSeller, setSelectedSeller] = useState<string>('all');
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });

  const [allSellers, setAllSellers] = useState<string[]>([]);

  useEffect(() => {
    fetchSales();
  }, [selectedRuta, startDate, endDate, user]);

  useEffect(() => {
    if (isRutaRestricted(user)) {
      const visible = getVisibleRutas(user);
      if (!visible.includes(selectedRuta)) setSelectedRuta(visible[0]);
    }
  }, [user]);

  const getFinalLoanAmount = (loanAmount: number) => loanAmount - 10;

  const fetchSales = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const startISO = new Date(startDate + 'T00:00:00').toISOString();
      const endISO = new Date(endDate + 'T23:59:59.999').toISOString();

      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          loan_amount,
          total_amount,
          created_at,
          ruta,
          seller:seller_id(email)
        `)
        .gte('created_at', startISO)
        .lte('created_at', endISO)
        .eq('approval_status', 'approved')
        .eq('unit_id', user?.unit_id);

      if (selectedRuta > 0) {
        query = query.eq('ruta', selectedRuta);
      }

      const { data: contracts, error: contractsError } = await query;

      if (contractsError) throw contractsError;

      // Collect unique CPFs to check for prior contracts
      const cpfs = [...new Set((contracts || []).map(c => c.cpf).filter(Boolean))];

      // Find CPFs that have an approved contract created BEFORE the selected start date
      const renewalCpfs = new Set<string>();
      if (cpfs.length > 0) {
        const batchSize = 200;
        for (let i = 0; i < cpfs.length; i += batchSize) {
          const batch = cpfs.slice(i, i + batchSize);
          const { data: prior } = await supabase
            .from('contracts')
            .select('cpf')
            .in('cpf', batch)
            .eq('approval_status', 'approved')
            .eq('unit_id', user?.unit_id)
            .lt('created_at', startISO);

          prior?.forEach(p => renewalCpfs.add(p.cpf));
        }

        // Also handle intra-period renewals: if same CPF appears more than once
        // in the current result set, the later ones are renewals
        const cpfFirstSeen = new Map<string, string>();
        const sorted = [...(contracts || [])].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        for (const c of sorted) {
          if (!c.cpf) continue;
          if (cpfFirstSeen.has(c.cpf)) {
            renewalCpfs.add(c.cpf + '::' + c.id);
          } else {
            cpfFirstSeen.set(c.cpf, c.id);
          }
        }
      }

      const sellers = new Set<string>();
      const salesByDate: { [key: string]: { [ruta: number]: DailySale } } = {};

      contracts?.forEach(contract => {
        const date = new Date(contract.created_at).toISOString().split('T')[0];
        const ruta = contract.ruta;
        const finalLoanAmount = getFinalLoanAmount(contract.loan_amount);
        const sellerEmail = (contract.seller as any)?.email || '';
        const isRenewal = renewalCpfs.has(contract.cpf) || renewalCpfs.has(contract.cpf + '::' + contract.id);

        if (sellerEmail) sellers.add(sellerEmail);

        if (!salesByDate[date]) salesByDate[date] = {};

        if (!salesByDate[date][ruta]) {
          salesByDate[date][ruta] = {
            date,
            ruta,
            totalContracts: 0,
            totalAmount: 0,
            contracts: []
          };
        }

        salesByDate[date][ruta].totalContracts += 1;
        salesByDate[date][ruta].totalAmount += finalLoanAmount;
        salesByDate[date][ruta].contracts.push({
          id: contract.id,
          client_name: contract.client_name,
          cpf: contract.cpf,
          loan_amount: finalLoanAmount,
          total_amount: contract.total_amount,
          created_at: contract.created_at,
          seller_email: sellerEmail,
          is_renewal: isRenewal
        });
      });

      const salesArray = Object.values(salesByDate)
        .flatMap(rutaSales => Object.values(rutaSales))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setAllSellers(Array.from(sellers).sort());
      setSales(salesArray);
    } catch (err) {
      console.error('Error fetching sales:', err);
      setError('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const filteredSales = useMemo(() => {
    if (selectedSeller === 'all') return sales;

    return sales
      .map(sale => {
        const filtered = sale.contracts.filter(c => c.seller_email === selectedSeller);
        if (filtered.length === 0) return null;
        return {
          ...sale,
          contracts: filtered,
          totalContracts: filtered.length,
          totalAmount: filtered.reduce((s, c) => s + c.loan_amount, 0)
        };
      })
      .filter(Boolean) as DailySale[];
  }, [sales, selectedSeller]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateString: string) =>
    new Date(dateString + 'T00:00:00').toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

  const totalMonth = filteredSales.reduce((t, s) => t + s.totalAmount, 0);
  const totalContracts = filteredSales.reduce((t, s) => t + s.totalContracts, 0);
  const avgPerContract = totalMonth / (totalContracts || 1);

  const { totalNew, totalRenewals } = useMemo(() => {
    let n = 0, r = 0;
    for (const sale of filteredSales) {
      for (const c of sale.contracts) {
        if (c.is_renewal) r++; else n++;
      }
    }
    return { totalNew: n, totalRenewals: r };
  }, [filteredSales]);

  const clearFilters = () => {
    setSelectedRuta(0);
    setSelectedSeller('all');
    const today = new Date();
    setStartDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
    setEndDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  };

  const hasActiveFilters = selectedRuta > 0 || selectedSeller !== 'all';

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Carregando vendas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Vendas Diarias</h1>
          <p className="text-sm text-gray-500 mt-0.5">Acompanhe contratos fechados, valores e medias por periodo</p>
        </div>

        {/* FILTERS */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Filtros</span>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {/* Ruta */}
            <div className="flex flex-col gap-1.5 min-w-[130px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ruta</label>
              <select
                value={selectedRuta}
                onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {!isRutaRestricted(user) && <option value={0}>Todas</option>}
                {getVisibleRutas(user).map(r => <option key={r} value={r}>Ruta {r}</option>)}
              </select>
            </div>

            {/* Seller */}
            <div className="flex flex-col gap-1.5 min-w-[180px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendedor</label>
              <select
                value={selectedSeller}
                onChange={(e) => setSelectedSeller(e.target.value)}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos</option>
                {allSellers.map(s => (
                  <option key={s} value={s}>{s.split('@')[0]}</option>
                ))}
              </select>
            </div>

            {/* Start date */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Data Inicial</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-10 pl-9 pr-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* End date */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Data Final</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-10 pl-9 pr-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Clear */}
            {hasActiveFilters && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-transparent select-none">x</label>
                <button
                  onClick={clearFilters}
                  className="h-10 flex items-center gap-1.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-sm font-medium transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Limpar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 col-span-2 sm:col-span-1">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Total Vendas</p>
                <p className="text-xl font-bold text-emerald-600 leading-tight">{formatCurrency(totalMonth)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Contratos</p>
                <p className="text-xl font-bold text-blue-600 leading-tight">{totalContracts}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Novos</p>
                <p className="text-xl font-bold text-green-600 leading-tight">{totalNew}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Renovacoes</p>
                <p className="text-xl font-bold text-amber-600 leading-tight">{totalRenewals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 col-span-2 sm:col-span-1">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <BarChart2 className="w-5 h-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Media</p>
                <p className="text-xl font-bold text-teal-600 leading-tight">{formatCurrency(avgPerContract)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* RESULT INFO */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="font-bold text-gray-800 text-base">{totalContracts}</span> contrato{totalContracts !== 1 ? 's' : ''} em <span className="font-semibold text-gray-700">{filteredSales.length}</span> dia{filteredSales.length !== 1 ? 's' : ''}
        </div>

        {/* DAILY SALES */}
        {filteredSales.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Calendar className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold">Nenhuma venda encontrada</p>
            <p className="text-sm text-gray-400 max-w-xs">
              Nenhum contrato aprovado encontrado para o periodo selecionado.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSales.map((sale) => {
              const dayNew = sale.contracts.filter(c => !c.is_renewal).length;
              const dayRenew = sale.contracts.filter(c => c.is_renewal).length;

              return (
                <div key={`${sale.date}-${sale.ruta}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

                  {/* Day header */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-5 py-4 bg-gray-50/60 border-b border-gray-100">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 capitalize leading-tight truncate">
                          {formatDate(sale.date)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                            Ruta {sale.ruta}
                          </span>
                          {dayNew > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                              <UserPlus className="w-3 h-3" />
                              {dayNew} {dayNew === 1 ? 'novo' : 'novos'}
                            </span>
                          )}
                          {dayRenew > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                              <RefreshCw className="w-3 h-3" />
                              {dayRenew} {dayRenew === 1 ? 'renov.' : 'renov.'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-left sm:text-right">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Contratos</p>
                        <p className="text-lg font-bold text-blue-600">{sale.totalContracts}</p>
                      </div>
                      <div className="w-px h-8 bg-gray-200 hidden sm:block" />
                      <div className="text-left sm:text-right">
                        <p className="text-xs text-gray-400 uppercase tracking-wide">Total</p>
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(sale.totalAmount)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contracts list */}
                  <div className="p-4 space-y-2.5">
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-3">
                      Contratos do dia
                    </p>
                    {sale.contracts.map((contract) => (
                      <div
                        key={contract.id}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/60 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            contract.is_renewal ? 'bg-amber-100' : 'bg-green-100'
                          }`}>
                            {contract.is_renewal
                              ? <RefreshCw className="w-4 h-4 text-amber-600" />
                              : <UserPlus className="w-4 h-4 text-green-600" />
                            }
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-bold text-gray-900 truncate">{contract.client_name}</p>
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ${
                                contract.is_renewal
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {contract.is_renewal ? 'Renov.' : 'Novo'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                              {contract.seller_email ? contract.seller_email.split('@')[0] : 'Vendedor nao informado'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 justify-between sm:justify-end flex-shrink-0">
                          <p className="text-sm font-bold text-emerald-600">
                            {formatCurrency(contract.loan_amount)}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-gray-400 bg-white border border-gray-100 px-2.5 py-1 rounded-lg">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span>{new Date(contract.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
